/**
 * Fixed-capacity object pool. All entities are preallocated up front so the
 * hot game loop never allocates (avoids GC stalls that would spike frame time
 * during dense bullet waves). Active entities live in a packed array; freeing
 * swaps the freed slot with the last active one (swap-remove, O(1)).
 */
export class Pool<T> {
  readonly items: T[];
  /** Number of currently-active items, packed at the front of `items`. */
  count = 0;

  constructor(capacity: number, factory: () => T) {
    this.items = new Array<T>(capacity);
    for (let i = 0; i < capacity; i++) this.items[i] = factory();
  }

  get capacity(): number {
    return this.items.length;
  }

  /** Activate and return the next free item, or null if exhausted. */
  spawn(): T | null {
    if (this.count >= this.items.length) return null;
    return this.items[this.count++];
  }

  /** Free the active item at index `i` via swap-remove. */
  freeAt(i: number): void {
    const last = --this.count;
    if (i !== last) {
      const tmp = this.items[i];
      this.items[i] = this.items[last];
      this.items[last] = tmp;
    }
  }

  clear(): void {
    this.count = 0;
  }
}
